import {useContext, useState} from "react";
import {Alert, Button, Divider, Modal, Radio, Space, Spin, Tag, notification} from "antd";
import {ConfiguratorContext} from "../../../context";
import {useQuoteContext} from "../../../contexts/QuoteContext";
import {useIntl} from "react-intl";
import {AssemblyOption, BaseCategory} from "../../../api/models";

interface DuplicateCategory {
  category: BaseCategory
  assemblyIds: number[]
}

const AlertDuplicateSelections = ( props: {
  categories: BaseCategory[] | undefined
  onQuoteChanged?: () => void
}) => {

  const configurator = useContext(ConfiguratorContext);
  const intl = useIntl();
  const {quote} = useQuoteContext();

  const [open, setOpen] = useState(false);
  const [loadingOptions, setLoadingOptions] = useState(false);
  const [optionsByCategory, setOptionsByCategory] = useState<Record<number, AssemblyOption[]>>({});
  const [keepByCategory, setKeepByCategory] = useState<Record<number, number | undefined>>({});
  const [fixingCategoryId, setFixingCategoryId] = useState<number>();

  const canRepair = configurator.isAdmin() || configurator.isEngineering();

  const duplicates: DuplicateCategory[] = (props.categories || [])
      .filter( c => !c.allowMultiple )
      .map( c => ({ category: c, assemblyIds: quote?.selections?.[ c.id ] || [] }) )
      .filter( d => d.assemblyIds.length > 1 );

  if ( !quote || !canRepair || !duplicates.length ) return <></>;

  const loadOptions = async () => {
    if ( !quote.model?.id ) return;
    setLoadingOptions(true);
    try {
      const byCategory: Record<number, AssemblyOption[]> = {};
      for ( const dup of duplicates ) {
        const resp = await configurator.api.fetchCategoryOptionsPage( quote.model.id, dup.category.id, {
          page: { size: 100 },
          filter: { showObsolete: true, showIncompatible: true },
          options: { quoteRevisionId: quote.displayRevisionId },
        });
        byCategory[ dup.category.id ] = (resp.data.content || []).filter( a => dup.assemblyIds.includes( a.id ) );
      }
      setOptionsByCategory( byCategory );
    }
    catch (e:any) {
      const errorMsg = intl.formatMessage({ id: e.response?.data?.message || e.message });
      notification.error({ message: "Failed to load selection details. " + errorMsg });
    }
    setLoadingOptions(false);
  };

  const handleOpen = () => {
    setOpen(true);
    loadOptions();
  };

  const handleFix = async (dup: DuplicateCategory) => {
    const keepAssemblyId = keepByCategory[ dup.category.id ];
    if ( !keepAssemblyId ) return;

    setFixingCategoryId( dup.category.id );
    try {
      await configurator.api.resolveDuplicateSelection( quote.displayRevisionId, dup.category.id, keepAssemblyId );
      notification.success({ message: `Removed extra selections from ${dup.category.name}.` });
      if ( duplicates.length === 1 ) setOpen(false);
      props.onQuoteChanged?.();
    }
    catch (e:any) {
      const errorMsg = intl.formatMessage({ id: e.response?.data?.message || e.message });
      notification.error({ message: "Failed to resolve selections. " + errorMsg });
    }
    setFixingCategoryId(undefined);
  };

  const describeAssembly = (dup: DuplicateCategory, assemblyId: number) => {
    const option = optionsByCategory[ dup.category.id ]?.find( a => a.id === assemblyId );
    if ( !option ) return <span>Assembly #{assemblyId}</span>;
    return <Space>
      <span>{option.bom}</span>
      <span>{option.label}</span>
      {!!option.obsoletedAt && <Tag color="red">Obsolete</Tag>}
      {!!option.incompatible && <Tag color="orange">Filtered by rules</Tag>}
    </Space>;
  };

  return <div style={{marginBottom: "1rem"}} data-testid="alertDuplicateSelections">
    <Alert
        type="error"
        showIcon
        message={<div>
          {duplicates.length === 1
              ? `More than one option is selected for single-selection category ${duplicates[0].category.name}.`
              : `More than one option is selected in ${duplicates.length} single-selection categories.`}
        </div>}
        action={<Button size="small" danger onClick={handleOpen}>Review &amp; Fix</Button>}
    />

    <Modal
        title="Resolve Duplicate Selections"
        open={open}
        onCancel={() => setOpen(false)}
        footer={<Button onClick={() => setOpen(false)}>Close</Button>}
        width={"50rem"}
    >
      <Spin spinning={loadingOptions}>
        {duplicates.map( (dup, ndx) => <div key={dup.category.id}>
          {ndx > 0 && <Divider />}
          <div style={{fontWeight: "bold", marginBottom: "0.5rem"}}>{dup.category.name}</div>
          <div style={{marginBottom: "0.5rem"}}>Choose the selection to keep. The others will be removed and the change recorded in the quote audit log.</div>
          <Radio.Group
              value={keepByCategory[ dup.category.id ]}
              onChange={(e) => setKeepByCategory({ ...keepByCategory, [ dup.category.id ]: e.target.value })}
          >
            <Space direction="vertical">
              {dup.assemblyIds.map( assemblyId =>
                  <Radio key={assemblyId} value={assemblyId}>{describeAssembly(dup, assemblyId)}</Radio>
              )}
            </Space>
          </Radio.Group>
          <div style={{marginTop: "0.75rem"}}>
            <Button
                type="primary"
                danger
                disabled={!keepByCategory[ dup.category.id ]}
                loading={fixingCategoryId === dup.category.id}
                onClick={() => handleFix(dup)}
            >Keep selection and remove the rest</Button>
          </div>
        </div>)}
      </Spin>
    </Modal>
  </div>;
}

export default AlertDuplicateSelections;
